import { useTheme } from "next-themes";
import React, { useEffect, useState } from "react";

export default function RTHMIntelligenceLogoVertical({
    className,
    fillColor,
    link = "/intelligence",
}: {
    className?: string;
    fillColor?: string;
    link?: string;
}) {
    const { theme, resolvedTheme } = useTheme();
    const [mounted, setMounted] = useState(false);

    // Use useEffect to handle client-side mounting
    useEffect(() => {
        setMounted(true);
    }, []);

    // Default colors for light and dark modes
    const lightModeColor = "#000000"; // Black for light mode
    const darkModeColor = "#FFFFFF"; // White for dark mode

    // During SSR and initial render, always use a consistent color (light mode)
    // Only after mounting on client, use the actual theme
    const logoColor =
        fillColor ||
        (mounted && (resolvedTheme === "dark" || theme === "dark")
            ? darkModeColor
            : lightModeColor);

    return (
        <a
            href={link}
            className="intelligence-logo"
            aria-label="Intelligence logo link"
        >
            <svg
                width="173"
                height="42"
                viewBox="0 0 173 42"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}
            >
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M70.9896 19.9015V24.0449C69.8762 24.6901 68.5887 25.0977 66.8141 25.0977C63.5781 25.0977 61.3859 23.8411 61.3859 19.6298V11.0036H56.6594C54.1987 11.0036 51.2614 13.7205 51.2614 18.0676V24.792H45.9724V6.58856H51.2614V10.2564C52.3122 7.80818 53.7635 6.64913 58.7544 6.59088C60.0457 6.4553 61.2025 5.70116 61.3859 4.49411V0H66.6749V4.48357C66.5249 5.78575 65.572 6.42326 65.0756 6.58531H66.6749V6.58856H71.0592V11.0036H66.6749V18.7808C66.6749 19.9695 67.1968 20.5468 68.38 20.5468C69.3542 20.5468 70.2241 20.3091 70.9896 19.9015ZM90.7295 24.792V13.0073C90.7295 8.83003 88.3981 6.24895 84.3967 6.24895C81.7173 6.24895 80.1516 7.64138 78.9337 9.16965V0H73.6447V24.792H78.9337V14.6375C78.9337 12.1922 80.2211 10.9357 82.2393 10.9357C84.2574 10.9357 85.4406 12.1922 85.4406 14.6375V24.792H90.7295ZM116.619 19.6278L116.604 19.6302C116.604 23.8414 118.797 25.098 122.033 25.098C123.807 25.098 125.095 24.6905 126.208 24.0452V19.9019C125.443 20.3094 124.573 20.5471 123.599 20.5471C122.53 20.5471 122.001 20.0765 121.908 19.1101V12.9394C121.908 8.55834 119.542 6.24895 115.61 6.24895C113.07 6.24895 111.122 7.2678 109.486 9.13569C108.512 7.30176 106.668 6.24895 104.232 6.24895C101.553 6.24895 99.9521 7.64138 98.7343 9.16965V6.58856H93.4453V24.792H98.7343V14.6375C98.7343 12.1922 99.9521 10.9357 101.935 10.9357C103.919 10.9357 105.032 12.1922 105.032 14.6375V24.792H110.321V14.6375C110.321 12.1922 111.539 10.9357 113.523 10.9357C115.506 10.9357 116.619 12.1922 116.619 14.6375V19.6278Z"
                    fill={logoColor}
                />
                <path
                    d="M1.07519 41.098H0V29.498H1.07519V41.098Z"
                    fill={logoColor}
                />
                <path
                    d="M17.3034 41.098L10.9326 30.65V41.098H9.85736V29.498H11.414L17.7849 39.962V29.498H18.8601V41.098H17.3034Z"
                    fill={logoColor}
                />
                <path
                    d="M30.18 30.49H26.2644V29.498H35.1708V30.49H31.2552V41.098H30.18V30.49Z"
                    fill={logoColor}
                />
                <path
                    d="M42.5637 29.498H50.0259V30.49H43.6389V34.586H49.6407V35.594H43.6389V40.106H50.0259V41.098H42.5637V29.498Z"
                    fill={logoColor}
                />
                <path
                    d="M59.0597 40.106H65.222V41.098H57.9845V29.498H59.0597V40.106Z"
                    fill={logoColor}
                />
                <path
                    d="M73.6968 40.106H79.8591V41.098H72.6217V29.498H73.6968V40.106Z"
                    fill={logoColor}
                />
                <path
                    d="M88.334 41.098H87.2588V29.498H88.334V41.098Z"
                    fill={logoColor}
                />
                <path
                    d="M106.488 32.986H105.365C104.883 31.05 103.262 30.314 101.642 30.314C99.3147 30.314 97.5174 32.09 97.5174 35.29C97.5174 38.378 99.1703 40.282 101.561 40.282C103.278 40.282 105.445 39.61 105.445 36.81V36.378H102.187V35.386H106.472V41.098H105.525L105.509 39.466C104.803 40.57 103.551 41.306 101.545 41.306C98.2074 41.306 96.378 38.794 96.378 35.29C96.378 31.738 98.3518 29.29 101.674 29.29C104.145 29.29 105.974 30.57 106.488 32.986Z"
                    fill={logoColor}
                />
                <path
                    d="M115.091 29.498H122.554V30.49H116.167V34.586H122.168V35.594H116.167V40.106H122.554V41.098H115.091V29.498Z"
                    fill={logoColor}
                />
                <path
                    d="M137.958 41.098L131.587 30.65V41.098H130.512V29.498H132.069L138.44 39.962V29.498H139.515V41.098H137.958Z"
                    fill={logoColor}
                />
                <path
                    d="M157.655 37.37C157.158 39.69 155.473 41.306 152.728 41.306C149.39 41.306 147.561 38.794 147.561 35.29C147.561 31.738 149.455 29.29 152.777 29.29C155.408 29.29 157.109 30.874 157.607 33.226H156.484C156.018 31.29 154.494 30.314 152.744 30.314C150.45 30.314 148.7 32.09 148.7 35.29C148.7 38.378 150.353 40.282 152.744 40.282C154.975 40.282 156.211 38.874 156.532 37.37H157.655Z"
                    fill={logoColor}
                />
                <path
                    d="M165.538 29.498H173V30.49H166.613V34.586H172.615V35.594H166.613V40.106H173V41.098H165.538V29.498Z"
                    fill={logoColor}
                />
            </svg>
        </a>
    );
}
